/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import CheckIcon from '@material-ui/icons/Check';
import RemoveIcon from '@material-ui/icons/Remove';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import naturalCompare from 'natural-compare-lite';

import globals from '../../../../globals/communityGlobals';
import { startEndTimeDisplay, getDateForApi, isValidDate } from '../../../../utils/dates';
import recurrenceTypes from '../../../../settings/constants/recurrenceTypes';
import { updateTreatmentInstance } from '../../../../actions/entities';
import useTimeline from '../../timeline/useTimeline';
import TreatmentImportStatusCheck from '../../../common/Integration/Integration';
import { DaysOfWeek, selectionTypes } from '../../../../enums';
import KeyboardTimeSelector from '../../../common/KeyboardDateTimeSelectors/KeyboardTimeSelector';
import { getErrorProps } from '../../../../utils/keyboardSelectorFunctions';

const errors = globals.getArea('error');

export function TreatmentRecurrenceSlots({ treatment, treatmentInstance, canEdit, includeTreatments, refreshGrid }) {
  const [editModeEnabled, setEditMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date(treatmentInstance.effectiveTimeSlots[0].from));
  const [selectedSlots, setSelectedSlots] = useState(Object.values(treatmentInstance.selectedTimeSlots)[0]);
  const [validationErrors, setValidationErrors] = useState({});

  const dispatch = useDispatch();
  const { refreshClinicianTreatmentsAndClosePanels } = useTimeline();

  const isImportedFromSystmOne = TreatmentImportStatusCheck(treatment);

  const daysOfWeekArray = Object.values(DaysOfWeek);
  const isDailyReccurence =
    treatment.recurrenceType === recurrenceTypes.DISABLED ||
    treatment.recurrenceType === recurrenceTypes.DAILY ||
    treatment.recurrenceType === recurrenceTypes.MONTHLY;
  const columnLength = isDailyReccurence ? ['Daily'] : daysOfWeekArray;
  const isMaster = treatmentInstance.masterId === null;
  const getDayIndex = day => (day === 0 ? 6 : day - 1);

  const selectionType = () => {
    if (treatment.isSpecific) return selectionTypes.specific;
    if (treatment.isMultiple) return selectionTypes.multiple;
    return selectionTypes.timeSlot;
  };

  const handleCancel = () => {
    setEditMode(!editModeEnabled);
    if (selectionType() === selectionTypes.specific) {
      setSelectedDate(new Date(treatmentInstance.effectiveTimeSlots[0].from));
    } else {
      setSelectedSlots(Object.values(treatmentInstance.selectedTimeSlots)[0]);
    }
  };

  const handleSave = () => {
    let updatedTreatment = {};
    if (selectionType() === selectionTypes.specific) {
      updatedTreatment = {
        treatmentId: treatment.id,
        treatmentInstanceId: treatmentInstance.treatmentInstanceId,
        isSpecific: true,
        occurenceOriginalDate: getDateForApi(treatmentInstance.occurrence),
        specificTimeFrom: getDateForApi(selectedDate),
      };
    } else {
      const selectedTimeSlots = { ...selectedSlots };
      Object.keys(selectedTimeSlots).forEach(tsKey => {
        if (!selectedTimeSlots[tsKey]) delete selectedTimeSlots[tsKey];
      });
      updatedTreatment = {
        treatmentId: treatment.id,
        treatmentInstanceId: treatmentInstance.treatmentInstanceId,
        isSpecific: false,
        occurenceOriginalDate: getDateForApi(treatmentInstance.occurrence),
        timeSlotsMatrix: { 0: selectedTimeSlots },
        timeSlotPackageId: treatmentInstance.timeSlotPackage.id,
      };
    }
    dispatch(updateTreatmentInstance(updatedTreatment)).then(() => {
      refreshClinicianTreatmentsAndClosePanels();
      if (includeTreatments) refreshGrid();
    });
  };

  const handleTimeSlotSelectionChange = slotId => {
    if (editModeEnabled) {
      const selectedTimeSlots = { ...selectedSlots };
      if (selectedTimeSlots[slotId]) delete selectedTimeSlots[slotId];
      else {
        selectedTimeSlots[slotId] = true;
      }
      setSelectedSlots(selectedTimeSlots);
    }
  };

  const handleDateChange = date => {
    setSelectedDate(date);
    setValidationErrors({ selectedDate: date ? null : errors.getMsg('requiredFieldValidationMessage') });
  };

  const treatmentRecurrenceSpecificRender = () => {
    return (
      <div className="clinician-info-box">
        <h6>Reccurence pattern</h6>
        <Box display="flex" alignItems="center" justifyContent="space-between" marginTop="1%">
          <Box>
            <KeyboardTimeSelector
              label="Treatment start"
              disabled={!editModeEnabled}
              value={selectedDate}
              onChange={handleDateChange}
              errorProps={getErrorProps(validationErrors.selectedDate)}
            />
          </Box>
          <Box display="flex" alignItems="center">
            <Box>
              {canEdit && !editModeEnabled && isMaster && selectionType() !== selectionTypes.multiple && (
                <Button
                  className="timeslots-button edit-button"
                  variant="contained"
                  color="primary"
                  onClick={() => setEditMode(!editModeEnabled)}
                >
                  Edit
                </Button>
              )}
            </Box>
            <Box>
              {editModeEnabled && (
                <Button className="timeslots-button cancel-button" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
            </Box>
            <Box>
              {editModeEnabled && (
                <Button
                  className="timeslots-button save-button"
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={!isValidDate(selectedDate)}
                >
                  Save
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {treatment.recurrenceType === recurrenceTypes.WEEKLY && Object.keys(treatment.selectedDays).length > 0 && (
          <div className="rostered-table">
            <Grid container spacing={1}>
              {daysOfWeekArray.map((day, index) => (
                <Grid item className="rostered-table-item" key={`rostered-table-item_${index}`}>
                  <span className="day">{day}</span>
                  <div className="rostered-time">
                    <Grid
                      item
                      style={{
                        backgroundColor:
                          treatment.selectedDays[index] && treatment.selectedDays[index][1] ? '#00fabc' : '#00c292',
                      }}
                      key={`rostered-time-item_${index}`}
                    >
                      {treatment.selectedDays[index] && treatment.selectedDays[index][1] ? (
                        <CheckIcon />
                      ) : (
                        <RemoveIcon />
                      )}
                    </Grid>
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
        )}
      </div>
    );
  };

  const treatmentRecurrenceSlotsRender = () => {
    const allSelected = treatmentInstance.timeSlotPackage.timeSlots.length === Object.keys(selectedSlots).length;
    const handleSelectAll = () => {
      if (!allSelected) {
        const updatedTimeslots = treatmentInstance.timeSlotPackage.timeSlots.reduce((arr, curr) => {
          return { ...arr, [curr.id]: true };
        }, {});
        setSelectedSlots(updatedTimeslots);
      } else {
        setSelectedSlots({});
      }
    };

    return treatmentInstance.timeSlotPackage ? (
      <div className="clinician-info-box">
        <h6>Treatment recurrence</h6>
        {canEdit && selectionType() !== selectionTypes.multiple && (
          <Box className="edit-timeslots-buttons" display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!editModeEnabled}
                    checked={allSelected}
                    color="primary"
                    onChange={handleSelectAll}
                    name="selectAll"
                  />
                }
                label="Select All"
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <Box>
                {!editModeEnabled && isMaster && !isImportedFromSystmOne && (
                  <Button
                    className="timeslots-button edit-button"
                    variant="contained"
                    color="primary"
                    onClick={() => setEditMode(!editModeEnabled)}
                  >
                    Edit
                  </Button>
                )}
              </Box>
              <Box>
                {editModeEnabled && (
                  <Button className="timeslots-button cancel-button" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </Box>
              <Box>
                {editModeEnabled && (
                  <Button
                    className="timeslots-button save-button"
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!isValidDate(selectedDate)}
                  >
                    Save
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
        <div className="rostered-table reccurence-patient">
          {columnLength.map((rowTitle, index) => (
            <Box spacing={1} key={`rostered-reccurence-item_${index}`}>
              <div className="day">
                <Box className="rostered-table-item">
                  {index === 0 && <span className="day">{''}</span>}
                  <Box>{rowTitle}</Box>
                </Box>
              </div>
              <Box flexWrap="wrap" display="flex" flexDirection="row" className="timeslotes-container">
                {(treatment.recurrenceType !== recurrenceTypes.WEEKLY ||
                  getDayIndex(selectedDate.getDay()) === index) &&
                  treatmentInstance.timeSlotPackage.timeSlots.map(slot => (
                    <Box className="rostered-table-item" key={`${index}_${slot.from}_${slot.to}`}>
                      <span className="day">{startEndTimeDisplay(slot.from, slot.to)}</span>
                      <div
                        className="rostered-time"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleTimeSlotSelectionChange(slot.id)}
                      >
                        <Box
                          style={{
                            backgroundColor: selectedSlots[slot.id] === true ? '#96E5B7' : '#E8E7E6',
                            padding: '8px',
                          }}
                        >
                          {slot.name}
                        </Box>
                      </div>
                    </Box>
                  ))}
              </Box>
              <Box flexWrap="wrap" display="flex" flexDirection="row" className="timeslotes-container">
                {treatment.recurrenceType === recurrenceTypes.WEEKLY &&
                  getDayIndex(selectedDate.getDay()) !== index &&
                  treatment.timeSlotsPackage.timeSlots
                    .sort((a, b) => naturalCompare(a.name, b.name))
                    .map((slot, i) => (
                      <Box className="rostered-table-item" key={`rostered-item_${index}_${i}`}>
                        <span className="day">{startEndTimeDisplay(slot.from, slot.to)}</span>
                        <div className="rostered-time" role="button" tabIndex={0}>
                          <Box
                            style={{
                              backgroundColor:
                                Object.keys(treatment.timeSlotsMatrix).includes(index.toString()) &&
                                Object.keys(treatment.timeSlotsMatrix[index]).includes(slot.id.toString()) &&
                                treatment.timeSlotsMatrix[index][slot.id] === true
                                  ? '#96E5B7'
                                  : '#E8E7E6',
                              padding: '8px',
                            }}
                          >
                            {slot.name}
                          </Box>
                        </div>
                      </Box>
                    ))}
              </Box>
            </Box>
          ))}
        </div>
      </div>
    ) : null;
  };

  return (
    <React.Fragment>
      {selectionType() === selectionTypes.specific
        ? treatmentRecurrenceSpecificRender()
        : treatmentRecurrenceSlotsRender()}
    </React.Fragment>
  );
}
